import { select } from '@ps/web-core-store';
import { utils } from '@ps/web-poker-tile-mappers';
import type { ActiveGameDataTourney } from '@ps/web-poker-types';
import { hasTournamentStarted } from '@ps/web-poker-utils';
import { Translation } from '@ps/web-shared-components';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import useTournamentTakeSeat from '../../hooks/takeSeat/useTournamentTakeSeat';
import useTournamentUnregister from '../../hooks/unregister/useTournamentUnregister';
import { TXTCLI_ON_DEMAND } from '../../translations';

import styles from './GameItem.module.scss';
import GameItemButtons from './GameItemButtons';
import sharedStyles from './shared.module.scss';
import { GAME_TYPE_TRANSLATIONS } from './utils/utils';

interface Props {
  game: ActiveGameDataTourney;
}

const GameItemTourney: React.FC<Props> = ({ game }) => {
  const unregister = useTournamentUnregister(+game.id);
  const takeSeat = useTournamentTakeSeat(+game.id);
  const { formatDate, formatMessage } = useIntl();
  const timeZoneName = useSelector(select.timeZone.getTimeZoneName);
  const { nowTimestamp, startTs } = utils.mttUtils.getMttStartTime({
    timeZone: timeZoneName,
    startTimestamp: game.startTime,
    lateRegTimestamp: 0
  });
  const hasStartTime = game.startTime !== -1;
  const formattedValue = hasStartTime
    ? formatDate(startTs * 1000, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
      }).replace(',', '')
    : formatMessage(TXTCLI_ON_DEMAND);
  const started = hasTournamentStarted(startTs, nowTimestamp);
  const [, setTick] = useState(Date.now());
  useEffect(() => {
    if (!started) {
      const interval = setInterval(() => {
        setTick(Date.now());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [started]);

  return (
    <div className={styles.wrap}>
      <div className={styles.titleWrap}>
        <p className={styles.type}>
          <Translation {...GAME_TYPE_TRANSLATIONS[game.type]} />
        </p>
        <p className={styles.title}>
          {formattedValue && (
            <>
              <span className={sharedStyles.state}>{formattedValue}</span>
              <span className={sharedStyles.separator}>&nbsp;|&nbsp;</span>
            </>
          )}
          <span>{game.name}</span>
        </p>
      </div>
      <GameItemButtons
        game={game}
        started={started}
        hasStartTime={hasStartTime}
        onUnregister={unregister}
        onTakeSeat={takeSeat}
        onOpen={takeSeat}
      />
    </div>
  );
};

export default GameItemTourney;
